import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { itToast as toast } from "../../utils/infinityToast";
import { ArrowLeft, Package, Upload, Check, Plus, Minus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getImageUrl } from "../../utils/imageUtils";
import { ENGRAVING_MAX_LEN } from "../../utils/validation";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fieldKey = (scope, cust, field) => `${scope}|${cust.customization_id}|${field.label}`;
const multiplierOf = (base, display) => (Number(base) > 0 ? Number(display) / Number(base) : 1);

// Raw price add-on for a single field given its selected value.
const fieldModifier = (field, value) => {
  if (field.field_type === "dropdown" || field.field_type === "radio") {
    return (field.options || []).find((o) => o.label === value)?.price_modifier || 0;
  }
  if (field.field_type === "checkbox") return value ? (field.options?.[0]?.price_modifier || 0) : 0;
  return 0;
};

/**
 * Product details (TR-9 3.3) — gallery + personalization. Keeps engraving 1/2 +
 * case-image picker AND renders the product's customizations (mandatory/
 * optional) + add-ons, modeled on the edisplayroom product page. Add-to-Cart from
 * here; each selected add-on becomes its own cart line.
 */
export default function InfinityProductDetails() {
  const { id } = useParams();
  const { http, isAuthed, addToCart, theme, basePath } = useInfinityCustomer();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [charges, setCharges] = useState({ engraving_charge: 0 });
  const [loading, setLoading] = useState(true);

  const [activeImg, setActiveImg] = useState(0);
  const [eng1, setEng1] = useState("");
  const [eng2, setEng2] = useState("");
  const [qty, setQty] = useState(1);
  const [customImage, setCustomImage] = useState("");     // product-level case image
  const [uploadingKey, setUploadingKey] = useState("");   // "" | 'product' | fieldKey
  const [values, setValues] = useState({});               // customization field values
  const [selectedAddons, setSelectedAddons] = useState([]);

  useEffect(() => {
    if (!isAuthed) { navigate(`${basePath}/login`); return; }
    Promise.all([
      http.get(`/product/${id}`),
      http.get("/case").catch(() => ({ data: { captures: [] } })),
      http.get("/charges").catch(() => ({ data: {} })),
    ]).then(([p, cse, ch]) => {
      setProduct(p.data);
      setCaptures(cse.data?.captures || []);
      setCharges(ch.data || {});
    }).catch(() => toast.error("Could not load the product")).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const setValue = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const primary = theme.primary_color || "#2E72BC";
  const accent = theme.accent_color || "#E0A93C";

  // Build the structured customization breakdown for a scope (main product or an
  // add-on). Returns { list, displayTotal } — list is what we send to the backend.
  const buildCustomizations = (scope, custObj, multiplier) => {
    const all = [...(custObj?.mandatory || []), ...(custObj?.optional || [])];
    const list = [];
    let displayTotal = 0;
    for (const cust of all) {
      const fields = [];
      let custDisplay = 0;
      for (const field of cust.form_fields || []) {
        const value = values[fieldKey(scope, cust, field)];
        const has = field.field_type === "checkbox" ? Boolean(value) : (value !== undefined && value !== "");
        if (!has) continue;
        const rawMod = fieldModifier(field, value);
        custDisplay += rawMod * multiplier;
        fields.push({ label: field.label, value, price_modifier: rawMod });
      }
      if (fields.length) {
        list.push({ customization_id: cust.customization_id, name: cust.name, fields, total: custDisplay });
        displayTotal += custDisplay;
      }
    }
    return { list, displayTotal };
  };

  // Validate that every required field of a mandatory customization has a value.
  const validateCustomizations = (scope, custObj) => {
    for (const cust of custObj?.mandatory || []) {
      for (const field of cust.form_fields || []) {
        if (!field.required) continue;
        const value = values[fieldKey(scope, cust, field)];
        const has = field.field_type === "checkbox" ? Boolean(value) : (value !== undefined && value !== "");
        if (!has) { toast.error(`Please complete "${field.label}" for ${cust.name}`); return false; }
      }
    }
    return true;
  };

  const priced = useMemo(() => {
    if (!product) return { main: 0, addons: 0, total: 0 };
    const mult = multiplierOf(product.base_price, product.display_price);
    const base = Number(product.display_price ?? product.base_price ?? 0);
    const eng = [eng1, eng2].filter((l) => l.trim()).length * (charges.engraving_charge || 0);
    const mainCust = buildCustomizations("main", product.customizations, mult).displayTotal;
    const mainUnit = base + mainCust + eng;

    let addonsTotal = 0;
    for (const ap of product.addons || []) {
      if (!selectedAddons.includes(ap.product_id)) continue;
      const amult = multiplierOf(ap.base_price, ap.display_price);
      const aCust = buildCustomizations(ap.product_id, ap.customizations, amult).displayTotal;
      addonsTotal += (Number(ap.display_price || 0) + aCust) * Math.max(1, qty);
    }
    return { main: mainUnit * Math.max(1, qty), addons: addonsTotal, total: mainUnit * Math.max(1, qty) + addonsTotal };
  }, [product, values, selectedAddons, eng1, eng2, qty, charges]); // eslint-disable-line

  const uploadTo = async (file, onUrl, key) => {
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await http.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUrl(data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingKey("");
    }
  };

  // A case-image picker (choose a case print or upload) — used by the product's
  // dedicated image input and by any image-type customization field.
  const CaseImagePicker = ({ value, onChange, uploadKey }) => (
    <div className="space-y-2">
      {captures.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {captures.map((c) => {
            const sel = value === c.image_url;
            return (
              <button key={c.capture_id} type="button" onClick={() => onChange(c.image_url)}
                className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 ${sel ? "border-blue-600" : "border-transparent"}`}>
                <img src={getImageUrl(c.image_url)} alt="Print" className="w-full h-full object-cover" />
                {sel ? <span className="absolute inset-0 bg-blue-600/20 flex items-center justify-center"><Check className="w-5 h-5 text-white" /></span> : null}
              </button>
            );
          })}
        </div>
      ) : <p className="text-xs text-slate-400">No case images available.</p>}
      <label className="inline-flex items-center gap-2 text-sm text-blue-700 cursor-pointer">
        <Upload className="w-4 h-4" /> {uploadingKey === uploadKey ? "Uploading…" : "Upload a different image"}
        <input type="file" accept="image/*" className="hidden" disabled={uploadingKey === uploadKey}
          onChange={(e) => e.target.files?.[0] && uploadTo(e.target.files[0], onChange, uploadKey)} />
      </label>
      {value ? (
        <div className="flex items-center gap-2 pt-1">
          <img src={getImageUrl(value)} alt="Selected" className="w-12 h-12 rounded object-cover border" />
          <button type="button" onClick={() => onChange("")} className="text-xs text-red-500">Deselect</button>
        </div>
      ) : null}
    </div>
  );

  // Render a single customization field (edisplayroom-parity types + IT image).
  const renderCustomField = (scope, cust, field) => {
    const key = fieldKey(scope, cust, field);
    const value = values[key];
    const modLabel = (m) => (m > 0 ? ` (+${money(m)})` : "");
    switch (field.field_type) {
      case "textarea":
        return <Textarea maxLength={field.character_limit || undefined} value={value || ""} onChange={(e) => setValue(key, e.target.value)} placeholder={field.placeholder} />;
      case "dropdown":
        return (
          <Select value={value || ""} onValueChange={(v) => setValue(key, v)}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map((o) => <SelectItem key={o.label} value={o.label}>{o.label}{modLabel(o.price_modifier)}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case "radio":
        return (
          <RadioGroup value={value || ""} onValueChange={(v) => setValue(key, v)} className="space-y-1">
            {(field.options || []).map((o) => (
              <label key={o.label} className="flex items-center gap-2 text-sm">
                <RadioGroupItem value={o.label} /> {o.label}{modLabel(o.price_modifier)}
              </label>
            ))}
          </RadioGroup>
        );
      case "checkbox":
        return (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={Boolean(value)} onCheckedChange={(c) => setValue(key, Boolean(c))} />
            {field.label}{modLabel(field.options?.[0]?.price_modifier || 0)}
          </label>
        );
      case "date":
        return <Input type="date" value={value || ""} onChange={(e) => setValue(key, e.target.value)} />;
      case "image":
        return <CaseImagePicker value={value || ""} onChange={(v) => setValue(key, v)} uploadKey={key} />;
      default: // text
        return <Input maxLength={field.character_limit || undefined} value={value || ""} onChange={(e) => setValue(key, e.target.value)} placeholder={field.placeholder} />;
    }
  };

  const renderCustGroup = (scope, custObj) => {
    const all = [...(custObj?.mandatory || []), ...(custObj?.optional || [])];
    if (!all.length) return null;
    return all.map((cust) => (
      <div key={cust.customization_id} className="space-y-3 border rounded-xl p-4">
        <p className="text-sm font-semibold text-slate-800">{cust.name}</p>
        {(cust.form_fields || []).map((field, i) => (
          <div key={i} className="space-y-1">
            {field.field_type !== "checkbox" ? (
              <Label className="text-xs">{field.label}{field.required ? " *" : ""}</Label>
            ) : null}
            {renderCustomField(scope, cust, field)}
          </div>
        ))}
      </div>
    ));
  };

  const add = () => {
    if (!validateCustomizations("main", product.customizations)) return;
    for (const ap of product.addons || []) {
      if (selectedAddons.includes(ap.product_id) && !validateCustomizations(ap.product_id, ap.customizations)) return;
    }
    const mult = multiplierOf(product.base_price, product.display_price);
    const mainCust = buildCustomizations("main", product.customizations, mult);

    // Main line
    addToCart({
      product,
      quantity: Math.max(1, qty),
      engraving_lines: [eng1, eng2].map((l) => l.trim()).filter(Boolean),
      custom_image_url: customImage || undefined,
      customizations: mainCust.list.length ? mainCust.list : undefined,
      customization_total: mainCust.displayTotal, // per-unit display add-on (cart math)
    });

    // Each selected add-on → its own cart line
    for (const ap of product.addons || []) {
      if (!selectedAddons.includes(ap.product_id)) continue;
      const amult = multiplierOf(ap.base_price, ap.display_price);
      const aCust = buildCustomizations(ap.product_id, ap.customizations, amult);
      addToCart({
        product: { product_id: ap.product_id, name: ap.name, display_price: ap.display_price, base_price: ap.base_price, image_url: ap.image_url },
        quantity: Math.max(1, qty),
        engraving_lines: [],
        customizations: aCust.list.length ? aCust.list : undefined,
        customization_total: aCust.displayTotal,
      });
    }
    // addToCart opens the slide-out drawer.
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 animate-spin" style={{ color: primary }} /></div>;
  if (!product) return null;

  const images = product.images?.length ? product.images : (product.image_url ? [product.image_url] : []);

  return (
    <div className="space-y-6">
      <Button variant="outline" size="sm" onClick={() => navigate(`${basePath}/shop`)}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to products
      </Button>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="aspect-square rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center">
            {images[activeImg] ? <img src={getImageUrl(images[activeImg])} alt={product.name} className="w-full h-full object-contain" /> : <Package className="w-16 h-16 text-slate-300" />}
          </div>
          {images.length > 1 ? (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-2 snap-x">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 shrink-0 snap-start rounded-lg overflow-hidden border-2 ${i === activeImg ? "border-slate-800" : "border-transparent"}`}>
                  <img src={getImageUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Details + personalization */}
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-slate-900">{product.name}</h1>
          <p className="text-xl font-semibold" style={{ color: primary }}>{money(product.display_price ?? product.base_price)}</p>
          {product.description ? <p className="text-sm text-slate-600 leading-relaxed">{product.description}</p> : null}

          {/* Product-level case image input (TR-8 2.4.2) */}
          {product.image_input_enabled ? (
            <div className="space-y-2 border rounded-xl p-4">
              <Label className="text-sm font-semibold">Select Fingerprint for this order</Label>
              <p className="text-xs text-slate-500">Choose one of the case's print images, or upload your own.</p>
              {/* The TR-21 "only if you are ordering fingerprint jewelry" caveat was
                  dropped in TR-22: the customer catalog now shows fingerprint products
                  only, so the warning described a case that can no longer arise. */}
              <CaseImagePicker value={customImage} onChange={setCustomImage} uploadKey="product" />
            </div>
          ) : null}

          {/* Product customizations (TR-9 3.3) */}
          {renderCustGroup("main", product.customizations)}

          {/* Engraving */}
          <div className="space-y-1">
            <Label className="text-xs">Engraving line 1 (optional)</Label>
            <Input maxLength={ENGRAVING_MAX_LEN} value={eng1} onChange={(e) => setEng1(e.target.value)} />
            <p className="text-[11px] text-slate-400 text-right">{eng1.length} / {ENGRAVING_MAX_LEN}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Engraving line 2 (optional)</Label>
            <Input maxLength={ENGRAVING_MAX_LEN} value={eng2} onChange={(e) => setEng2(e.target.value)} />
            <p className="text-[11px] text-slate-400 text-right">{eng2.length} / {ENGRAVING_MAX_LEN}</p>
          </div>
          <p className="text-[11px] text-slate-400">Engraving is {money(charges.engraving_charge)} per line.</p>

          {/* Add-ons (TR-9 3.3) */}
          {(product.addons || []).length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">Enhance your purchase</p>
              {product.addons.map((ap) => {
                const sel = selectedAddons.includes(ap.product_id);
                return (
                  <div key={ap.product_id} className={`rounded-xl border p-3 ${sel ? "border-slate-900" : ""}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox checked={sel} onCheckedChange={(c) => setSelectedAddons((s) => c ? [...s, ap.product_id] : s.filter((x) => x !== ap.product_id))} />
                      <div className="w-10 h-10 rounded bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                        {ap.image_url ? <img src={getImageUrl(ap.image_url)} alt={ap.name} className="w-full h-full object-contain" /> : <Package className="w-4 h-4 text-slate-300" />}
                      </div>
                      <div className="flex-1 text-sm">
                        <p className="text-slate-800">{ap.name}</p>
                        <p className="text-xs" style={{ color: accent }}>+{money(ap.display_price)}</p>
                      </div>
                    </label>
                    {sel ? <div className="mt-3 space-y-3">{renderCustGroup(ap.product_id, ap.customizations)}</div> : null}
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className="space-y-1">
            <Label className="text-xs">Quantity</Label>
            <div className="inline-flex items-center border rounded-lg overflow-hidden">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-slate-50" aria-label="Decrease quantity">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-12 text-center text-sm font-medium">{qty}</span>
              <button type="button" onClick={() => setQty((q) => q + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-slate-50" aria-label="Increase quantity">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Button className="w-full text-white" style={{ backgroundColor: primary }} onClick={add}>
            Add to Cart · {money(priced.total)}
          </Button>
        </div>
      </div>
    </div>
  );
}
